import { auth } from '../config/firebaseConfig';
import { Todo } from '../types';

export interface UserProfile {
  id: string;
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL?: string | null;
  focus?: string;
  todos?: Todo[];
  timezone?: string | null;
  locale?: string | null;
  preferences?: Record<string, unknown>;
  createdAt?: unknown;
  updatedAt?: unknown;
}

const API_BASE_URL = process.env.REACT_APP_PROFILE_API_BASE_URL;
const DEBUG_API = process.env.REACT_APP_DEBUG_AUTH === 'true';

const isProfileApiConfigured = (): boolean => !!API_BASE_URL;

const getIdToken = async (): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No Firebase user session found. Sign in again.');
  }
  return currentUser.getIdToken();
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_PROFILE_API_BASE_URL is not configured');
  }

  const token = await getIdToken();
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.log('[profileApi] request', { path, endpoint: API_BASE_URL, hasToken: !!token, method: init?.method || 'GET' });
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });

  const json = await res.json();
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.log('[profileApi] response', { path, status: res.status, ok: res.ok, error: json?.error || null });
  }
  if (!res.ok) {
    throw new Error(json?.error || 'Profile API request failed');
  }

  return json as T;
};

export const getMyProfile = async (): Promise<UserProfile> => {
  const data = await request<{ profile: UserProfile }>('/api/profile/me');
  return data.profile;
};

export const updateMyProfile = async (payload: {
  displayName?: string;
  focus?: string;
  todos?: Todo[];
  timezone?: string;
  locale?: string;
  preferences?: Record<string, unknown>;
}): Promise<UserProfile> => {
  const data = await request<{ profile: UserProfile }>('/api/profile/me', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return data.profile;
};

const profileApi = {
  isProfileApiConfigured,
  getMyProfile,
  updateMyProfile,
};

export default profileApi;
